// src/api/weopenReadApi.ts
//
// CRM → WeOpen READ-ONLY client (WeOpen ↔ CRM Unibox, Phase 3 PR 2).
//
// Talks ONLY to the PR1 BFF at `/api/weopen/*`, same-origin through `baseApi`
// (cookie session). The browser holds no Central token; the CRM backend forwards
// the acting user's Central bearer and WeOpen decides visibility.
//
// ── DELIBERATELY NOT USED HERE ──────────────────────────────────────────────
// `liveChatApi`, `conversationsApi`'s WeOpen endpoints, and anything under
// `/api/channels/weopen` belong to the PRE-EXISTING webhook-fed CRM mirror. That
// surface keeps its own locally-written session status and assigned-user column —
// a second assignment authority that the locked architecture assigns to WeOpen.
// Reading it here would resurrect exactly the competing-authority problem this
// integration exists to remove. A source-guard test pins that.
//
// ── NO MUTATIONS ────────────────────────────────────────────────────────────
// No send / claim / release / return / close. WeOpen owns the lifecycle, and the
// BFF exposes GETs only.
//
// ── CONVERGENCE (Phase 4 PR 4) ──────────────────────────────────────────────
//
// Authoritative REST stays the only source of truth. Convergence is polling plus
// cache-tag invalidation — NOT sockets: WeOpen's Socket.IO handshake verifies a
// symmetric secret (`jwt.verify(token, config.jwt.secret)`) and has no RS256/JWKS
// path, so a Central-mode agent token cannot authenticate to it. Activating that
// would be a WeOpen change, which this work is not permitted to make.
//
// Tags exist so a mutation converges every subscriber declaratively, instead of
// each caller remembering which three queries to refetch.
//
// ── TYPES FOLLOW WEOPEN'S ACTUAL SERIALIZATION ──────────────────────────────
// Modelled against the audited WeOpen controller serialization, not guessed.
// Two consequences worth stating, because both are easy to get wrong:
//
//   1. INBOX ROWS AND DETAIL ARE DIFFERENT SHAPES. An inbox row has NO
//      `crmContactId`, no workspaceId, no visitor display name and no
//      unreadCount. Only the detail read carries `crmContactId`. Code that
//      assumes one shape for both will silently render blanks.
//   2. There is no friendly visitor name anywhere. `externalUserId` is the only
//      customer-side identifier on a list row, and
//      `assignedAgentDisplayName` is the AGENT — never the customer.
//
// Only the fields this UI consumes are declared. `metadata` stays opaque: it
// carries internal analytics and a routing snapshot that CRM must not depend on.

import { baseApi } from './baseApi';

/** Lifecycle as WeOpen reports it. */
export type WeOpenStatus = 'bot' | 'human' | 'closed';

/** `status` query — which lifecycle states to include. */
export type WeOpenInboxStatus = 'open' | 'closed' | 'all';

/**
 * `filter` query — WeOpen's inbox SCOPE.
 *
 * WeOpen's server default is `assigned`. An inbox fetched without this parameter
 * therefore returns only what the agent already holds and omits every waiting
 * handoff, so it is always sent explicitly.
 */
export type WeOpenInboxFilter = 'assigned' | 'unassigned' | 'monitoring';

/** A row from `GET /api/v1/agent/inbox`. Deliberately narrow. */
export interface WeOpenInboxItem {
  id: string;
  botId: string;
  botName?: string;
  /** The only customer-side identifier on a list row. Legitimately null. */
  externalUserId: string | null;
  status: WeOpenStatus;
  /** null while a human handoff is still waiting to be claimed. */
  assignedAgentId: string | null;
  /** The AGENT's name. Never the customer's. */
  assignedAgentDisplayName: string | null;
  handoffReason: string | null;
  csatScore: number | null;
  lastMessagePreview?: string;
  lastMessageAt?: string;
  createdAt: string;
  updatedAt: string;
}

/** `GET /api/v1/conversations/:id`. Only the fields this UI reads. */
export interface WeOpenConversationDetail {
  id: string;
  workspaceId?: string;
  botId?: string;
  /** Present on DETAIL only — never on an inbox row. */
  crmContactId: string | null;
  externalUserId: string | null;
  mode?: string;
  status: WeOpenStatus;
  assignedAgentId: string | null;
  handoffReason: string | null;
  createdAt?: string;
  updatedAt?: string;
  bot?: { id: string; name: string; status?: string } | null;
  workspace?: { id: string; name: string } | null;
  /** Nested on detail, unlike the flat display name on an inbox row. */
  assignedAgent?: { id: string; email?: string; name?: string } | null;
  /** Opaque: internal analytics / routing snapshot. Never depended upon. */
  metadata?: Record<string, unknown> | null;
}

/** A message from `GET /api/v1/conversations/:id/messages` (createdAt ASC, id ASC). */
export interface WeOpenMessage {
  id: string;
  conversationId?: string;
  senderType: 'user' | 'bot' | 'agent';
  senderId?: string | null;
  /** The body. WeOpen calls it `content`; there is no text/body/message field. */
  content: string;
  role: 'user' | 'assistant' | 'system';
  intentLabel?: string | null;
  handledFlag?: boolean | null;
  readByAgentAt?: string | null;
  readByCustomerAt?: string | null;
  /** Opaque. May carry `isSystemMessage`, which is a hint and never required. */
  metadata?: Record<string, unknown> | null;
  createdAt: string;
  updatedAt?: string;
}

// ── Wire envelopes ──────────────────────────────────────────────────────────
//
// WeOpen answers `{ success, data: … }`; the BFF forwards the `data` field under
// its own `{ data: … }`. So one unwrap here reaches WeOpen's payload.

export interface WeOpenInboxResponse {
  conversations?: WeOpenInboxItem[] | null;
  /** WeOpen supplies a count. There is NO pagination — no page/limit/cursor. */
  meta?: { count?: number } | null;
}

export interface WeOpenMessagesResponse {
  messages?: WeOpenMessage[] | null;
}

interface DataEnvelope<T> {
  data: T;
}

/** WeOpen's reply to an agent send. `data` is unwrapped by the BFF. */
export interface WeOpenSendResponse {
  message: {
    id: string;
    conversationId: string;
    senderType: 'agent';
    content: string;
    createdAt: string;
  };
  /**
   * TRUE when the conversation was BOT-handled and this send JOINED it: WeOpen
   * transitioned it to `human` and assigned it to the sender. Sending acquired
   * ownership — a real consequence the UI must not hide.
   */
  agentInitiatedJoin: boolean;
  conversationStatus: WeOpenStatus;
}

export interface SendAgentMessageArgs {
  conversationId: string;
  content: string;
  /**
   * STABLE per logical message. A retry of the same message MUST reuse it, so
   * WeOpen replays instead of delivering a second copy; a new message must get a
   * new one. Generated by the caller, never here — a key minted inside the
   * mutation would change on every retry and defeat the whole mechanism.
   */
  idempotencyKey: string;
}

/** WeOpen's answer to a release. Ownership facts only — CRM stores none of it. */
export interface WeOpenReleaseResponse {
  conversationId: string;
  status: WeOpenStatus;
  handoffState?: string;
  released: boolean;
  previousAgentId: string | null;
}

export const weopenReadApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // GET /api/weopen/conversations?status=…&filter=…
    //
    // Both parameters are allow-listed by the BFF, which 400s anything else.
    // There is no search and no pagination in the contract, so none is offered.
    getWeOpenConversations: builder.query<
      WeOpenInboxItem[],
      { status?: WeOpenInboxStatus; filter?: WeOpenInboxFilter } | void
    >({
      query: (arg) => {
        const a = (arg ?? {}) as { status?: WeOpenInboxStatus; filter?: WeOpenInboxFilter };
        const qs = new URLSearchParams({ status: a.status ?? 'open' });
        // Always explicit: omitting it lets WeOpen's `assigned` default hide the
        // waiting queue.
        if (a.filter) qs.set('filter', a.filter);
        return { url: `/weopen/conversations?${qs.toString()}`, method: 'GET' };
      },
      // Normalised to an array so the view never branches on envelope shape. An
      // absent list becomes [] — rendered as "no conversations", NOT as an
      // error, because the request itself succeeded.
      transformResponse: (res: DataEnvelope<WeOpenInboxResponse> | undefined) =>
        res?.data?.conversations ?? [],
      // Phase 4 PR4 — declarative convergence. A mutation invalidating LIST
      // refreshes every subscriber, so no caller has to remember to refetch.
      providesTags: [{ type: 'WeOpenConversation', id: 'LIST' }],
    }),

    // GET /api/weopen/conversations/:id — the canonical detail read. Used even
    // though a list row exists, because only detail carries `crmContactId`.
    getWeOpenConversation: builder.query<WeOpenConversationDetail | null, string>({
      query: (id) => ({ url: `/weopen/conversations/${encodeURIComponent(id)}`, method: 'GET' }),
      transformResponse: (res: DataEnvelope<WeOpenConversationDetail> | undefined) =>
        res?.data ?? null,
      providesTags: (_r, _e, id) => [{ type: 'WeOpenConversation', id }],
    }),

    // GET /api/weopen/conversations/:id/messages
    getWeOpenConversationMessages: builder.query<WeOpenMessage[], string>({
      query: (id) => ({
        url: `/weopen/conversations/${encodeURIComponent(id)}/messages`,
        method: 'GET',
      }),
      transformResponse: (res: DataEnvelope<WeOpenMessagesResponse> | undefined) =>
        res?.data?.messages ?? [],
      providesTags: (_r, _e, id) => [{ type: 'WeOpenMessages', id }],
    }),

    // POST /api/weopen/conversations/:id/messages
    //
    // Deliberately does NOT optimistically insert a message. The authoritative
    // row is WeOpen's, and an optimistic copy would need reconciliation the
    // current read architecture has no mechanism for — a duplicate or a ghost
    // message is worse than a brief wait. On success the transcript is
    // invalidated and refetched instead.
    sendWeOpenAgentMessage: builder.mutation<WeOpenSendResponse, SendAgentMessageArgs>({
      query: ({ conversationId, content, idempotencyKey }) => ({
        url: `/weopen/conversations/${encodeURIComponent(conversationId)}/messages`,
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKey },
        body: { content },
      }),
      transformResponse: (res: DataEnvelope<WeOpenSendResponse>) => res.data,
      // The transcript AND the list row's last-message preview both change.
      invalidatesTags: (_r, _e, { conversationId }) => [
        { type: 'WeOpenMessages', id: conversationId },
        { type: 'WeOpenConversation', id: 'LIST' },
      ],
    }),

    // POST /api/weopen/conversations/:id/claim
    //
    // Explicit self-claim. No idempotency key: WeOpen has none for claim, and it
    // does not need one — a repeat answers 409 CONVERSATION_ALREADY_ASSIGNED, and
    // the row lock upstream makes the race first-success-wins. CRM must never try
    // to resolve that race itself.
    claimWeOpenConversation: builder.mutation<unknown, { conversationId: string }>({
      query: ({ conversationId }) => ({
        url: `/weopen/conversations/${encodeURIComponent(conversationId)}/claim`,
        method: 'POST',
      }),
      // Ownership moves, so the row leaves Waiting and the detail changes. The
      // join notice is a message, so the transcript changes too.
      invalidatesTags: (_r, _e, { conversationId }) => [
        { type: 'WeOpenConversation', id: 'LIST' },
        { type: 'WeOpenConversation', id: conversationId },
        { type: 'WeOpenMessages', id: conversationId },
      ],
    }),

    // POST /api/weopen/conversations/:id/release
    //
    // Release back to the waiting queue: still human, owner cleared. WeOpen also
    // adjusts load, handoff generation, wait timers and notifications — none of
    // which CRM touches or duplicates.
    // POST /api/weopen/conversations/:id/close
    //
    // Ends the conversation. `summary` is optional — the only body field the
    // upstream contract accepts, stored as `metadata.closureSummary`. Owner-only
    // upstream and IRREVERSIBLE: WeOpen exposes no reopen.
    closeWeOpenConversation: builder.mutation<
      unknown,
      { conversationId: string; summary?: string }
    >({
      query: ({ conversationId, summary }) => ({
        url: `/weopen/conversations/${encodeURIComponent(conversationId)}/close`,
        method: 'POST',
        body: summary ? { summary } : {},
      }),
      invalidatesTags: (_r, _e, { conversationId }) => [
        { type: 'WeOpenConversation', id: 'LIST' },
        { type: 'WeOpenConversation', id: conversationId },
        { type: 'WeOpenMessages', id: conversationId },
      ],
    }),

    releaseWeOpenConversation: builder.mutation<WeOpenReleaseResponse, { conversationId: string }>({
      query: ({ conversationId }) => ({
        url: `/weopen/conversations/${encodeURIComponent(conversationId)}/release`,
        method: 'POST',
      }),
      transformResponse: (res: DataEnvelope<WeOpenReleaseResponse>) => res.data,
      invalidatesTags: (_r, _e, { conversationId }) => [
        { type: 'WeOpenConversation', id: 'LIST' },
        { type: 'WeOpenConversation', id: conversationId },
        { type: 'WeOpenMessages', id: conversationId },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetWeOpenConversationsQuery,
  useGetWeOpenConversationQuery,
  useGetWeOpenConversationMessagesQuery,
  useSendWeOpenAgentMessageMutation,
  useClaimWeOpenConversationMutation,
  useReleaseWeOpenConversationMutation,
  useCloseWeOpenConversationMutation,
} = weopenReadApi;
